import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../services/firebase';

export default function EekoGateway() {
  const [searchParams] = useSearchParams();
  const orgId = searchParams.get('org'); // Dynamically captures the target tenant ID from the URL

  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  // Form State
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    taxId: '', // SSN or EIN
    taxClassification: 'Individual/Sole Proprietor',
    systemRole: 'ASSOCIATE' // Hardcoded to prevent external privilege escalation
  });

  // --- FATAL ERROR UI: NO ORG ID ---
  if (!orgId) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#070B14]">
        <div className="bg-slate-900 border border-red-500/50 p-8 max-w-md text-center shadow-2xl relative overflow-hidden">
          <div className="absolute inset-0 bg-red-500/5 animate-pulse"></div>
          <i className="fa-solid fa-triangle-exclamation text-red-500 text-4xl mb-4 relative z-10"></i>
          <h2 className="text-xl font-black text-white uppercase tracking-widest mb-2 relative z-10">Invalid Invite Link</h2>
          <p className="text-sm text-slate-400 font-medium relative z-10">
            This onboarding gateway requires a valid organization token. Please request a new provisioning link from your Master Agent.
          </p>
        </div>
      </div>
    );
  }

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleFinalSubmit = async () => {
    setLoading(true);
    try {
      // Call the backend engine to mint the new 1099 ID, passing the dynamic orgId from the URL
      const provisionNewRep = httpsCallable(functions, 'provisionNewRep');
      const response = await provisionNewRep({ 
        workerType: '1099',
        dealerId: orgId, // Injects the target Multi-Tenant Vault
        contractorData: formData // Passes the strict 'ASSOCIATE' role safely to the backend
      });
      
      // Save data to Firestore & trigger SignNow API (Placeholder)
      setResult({ success: true, repId: response.data.rep_id });
      setStep(4); // Move to completion screen
    } catch (error) {
      console.error("Onboarding failed:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#070B14] bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-slate-900 via-[#070B14] to-[#070B14] overflow-y-auto py-12">
      
      {/* Background Grid for depth */}
      <div className="fixed inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(to right, #1E293B 1px, transparent 1px), linear-gradient(to bottom, #1E293B 1px, transparent 1px)',
        backgroundSize: '40px 40px', opacity: 0.1
      }}></div>

      <div className="w-full max-w-2xl bg-slate-900 border border-slate-700 shadow-2xl relative z-10 p-10">
        
        {/* Brand Header */}
        <div className="flex flex-col items-center mb-8 border-b border-slate-800 pb-8">
          <div className="w-10 h-10 mb-4">
            <svg viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M16 2L2 9L16 16L30 9L16 2Z" fill="#10B981" fillOpacity="0.1" stroke="#10B981" strokeWidth="1.5" strokeLinejoin="round"/>
              <path d="M2 23L16 30L30 23V9L16 16L2 9V23Z" fill="#0F172A" />
              <path d="M2 23L16 30L30 23" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M16 30V16" stroke="#10B981" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              <circle cx="16" cy="16" r="2.5" fill="#22D3EE" className="animate-pulse" />
            </svg>
          </div>
          <h2 className="text-2xl font-black text-white tracking-widest uppercase mb-1">eeko Gateway</h2>
          <div className="text-[9px] font-bold tracking-[0.2em] text-slate-500 uppercase whitespace-nowrap">
            1099 Operator Onboarding & Verification
          </div>
          
          {/* Strict Progress Bar */}
          <div className="w-full flex gap-1 mt-8">
            <div className={`h-1 flex-1 ${step >= 1 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-slate-800'}`}></div>
            <div className={`h-1 flex-1 ${step >= 2 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-slate-800'}`}></div>
            <div className={`h-1 flex-1 ${step >= 3 ? 'bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.5)]' : 'bg-slate-800'}`}></div>
          </div>
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h3 className="text-[10px] font-bold text-emerald-500 uppercase tracking-[0.2em] mb-4">Step 01 // Identity Profile</h3>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">First Name</label>
                <input type="text" name="firstName" value={formData.firstName} onChange={handleInputChange} className="w-full bg-[#0B1120] border border-slate-700 p-3 text-white text-sm font-bold focus:outline-none focus:border-emerald-500 transition-all" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Last Name</label>
                <input type="text" name="lastName" value={formData.lastName} onChange={handleInputChange} className="w-full bg-[#0B1120] border border-slate-700 p-3 text-white text-sm font-bold focus:outline-none focus:border-emerald-500 transition-all" />
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Business Email</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} className="w-full bg-[#0B1120] border border-slate-700 p-3 text-white text-sm font-bold focus:outline-none focus:border-emerald-500 transition-all" />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Direct Phone</label>
                <input type="text" name="phone" value={formData.phone} onChange={handleInputChange} className="w-full bg-[#0B1120] border border-slate-700 p-3 text-white text-sm font-bold focus:outline-none focus:border-emerald-500 transition-all" />
              </div>
            </div>

            <button onClick={() => setStep(2)} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs py-4 px-4 uppercase tracking-[0.2em] transition-colors mt-4">
              Initialize Identity & Continue
            </button>
          </div>
        )}

        {/* Step 2: W-9 Capture */}
        {step === 2 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h3 className="text-[10px] font-bold text-emerald-500 uppercase tracking-[0.2em] mb-4">Step 02 // W-9 Certification</h3>
            
            <div className="bg-amber-900/10 border border-amber-500/30 p-4 mb-6">
              <p className="text-[9px] font-bold text-amber-500 uppercase tracking-widest flex items-center gap-2">
                <i className="fa-solid fa-lock"></i> Encrypted Vault Entry
              </p>
              <p className="text-xs text-amber-500/80 mt-1 font-medium">This data is encrypted using AES-256 before being written to the master database.</p>
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Federal SSN or EIN</label>
              <input type="password" name="taxId" value={formData.taxId} onChange={handleInputChange} className="w-full bg-[#0B1120] border border-slate-700 p-3 text-white text-sm font-bold focus:outline-none focus:border-emerald-500 transition-all tracking-[0.3em]" placeholder="XXX-XX-XXXX" />
            </div>

            <div>
              <label className="block text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-2">Tax Classification</label>
              <select name="taxClassification" value={formData.taxClassification} onChange={handleInputChange} className="w-full bg-[#0B1120] border border-slate-700 p-3 text-white text-sm font-bold focus:outline-none focus:border-emerald-500 transition-all cursor-pointer">
                <option value="Individual/Sole Proprietor">Individual / Sole Proprietor</option>
                <option value="C Corporation">C Corporation</option>
                <option value="S Corporation">S Corporation</option>
                <option value="Partnership">Partnership</option>
                <option value="LLC">Limited Liability Company (LLC)</option>
              </select>
            </div>

            <div className="flex gap-4 pt-2">
              <button onClick={() => setStep(1)} className="w-1/3 bg-[#0B1120] hover:bg-slate-800 text-slate-300 border border-slate-700 font-bold text-xs py-4 px-4 uppercase tracking-[0.2em] transition-colors">
                Back
              </button>
              <button onClick={() => setStep(3)} className="w-2/3 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs py-4 px-4 uppercase tracking-[0.2em] transition-colors">
                Verify & Continue
              </button>
            </div>
          </div>
        )}

        {/* Step 3: ICA Signature Block */}
        {step === 3 && (
          <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
            <h3 className="text-[10px] font-bold text-emerald-500 uppercase tracking-[0.2em] mb-4">Step 03 // Master Service Agreement</h3>
            
            <div className="h-64 bg-[#0B1120] border border-slate-700 flex items-center justify-center flex-col relative overflow-hidden">
              <div className="absolute inset-0 bg-[linear-gradient(45deg,transparent_25%,rgba(16,185,129,0.05)_50%,transparent_75%,transparent_100%)] bg-[length:20px_20px]"></div>
              <i className="fa-solid fa-file-signature text-3xl text-slate-600 mb-3"></i>
              <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest text-center">SignNow API Bridge</p>
              <p className="text-xs text-slate-500 mt-1 font-medium">Awaiting document generation & digital signature injection...</p>
            </div>

            <div className="flex gap-4 pt-2">
              <button onClick={() => setStep(2)} className="w-1/3 bg-[#0B1120] hover:bg-slate-800 text-slate-300 border border-slate-700 font-bold text-xs py-4 px-4 uppercase tracking-[0.2em] transition-colors">
                Back
              </button>
              <button onClick={handleFinalSubmit} disabled={loading} className="w-2/3 bg-emerald-600 hover:bg-emerald-500 text-white font-black text-xs py-4 px-4 uppercase tracking-[0.2em] transition-colors disabled:opacity-50 border border-emerald-400/20">
                {loading ? 'Executing...' : 'Sign & Finalize'}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Success State */}
        {step === 4 && result && (
          <div className="text-center py-10 animate-in zoom-in-95 duration-500">
            <div className="w-20 h-20 bg-emerald-500/10 text-emerald-500 border border-emerald-500/30 flex items-center justify-center mx-auto mb-6">
              <i className="fa-solid fa-check text-3xl"></i>
            </div>
            <h3 className="text-2xl font-black text-white uppercase tracking-widest mb-2">Provisioning Complete</h3>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-[0.2em] mb-8">System Access Granted</p>
            
            <div className="inline-block p-6 bg-[#0B1120] border border-slate-700 w-full max-w-xs">
              <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest mb-2">Assigned Operator ID</p>
              <p className="text-3xl font-black text-emerald-400 tracking-wider">{result.repId}</p>
            </div>

            <div className="mt-8">
              <button onClick={() => window.location.href = '/'} className="bg-slate-800 hover:bg-slate-700 border border-slate-600 text-white font-bold text-[10px] py-3 px-8 uppercase tracking-[0.2em] transition-colors">
                Enter Workspace
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}